import React, { useEffect, useMemo } from 'react';
import Input from '@/components/General/inputs/Input';
import { saveForm } from '@/data/forms';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Toast from 'react-native-toast-message';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import ToggleRow from '@/components/General/inputs/ToggleRow';
import { useEditScreenStore } from '@/data/store/useEditScreenStore';
import { redirectToDefault } from '@/utils/constants/linking';
import { useLocalSearchParams, useRouter, usePathname } from 'expo-router';
import ModalWrapper from '@/components/General/ModalWrapper';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { areStringsPopulated } from '@/utils/InputValidator';
import { createEmptySchema } from '@/utils/formsSchema';
import FormRolesPicker from '@/components/Forms/FormRolesPicker';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import { buildFolderTree, flattenFolderTree, folderPath } from '@/utils/formFolders';

const EMPTY_DRAFT = {};

export default function CreateFormModal() {
    const { id, folderID } = useLocalSearchParams();
    const storeKey = id || 'new';

    const setEditScreenData = useEditScreenStore(s => s.setEditScreenData);
    const removeEditScreenData = useEditScreenStore(s => s.removeEditScreenData);
    const details = useEditScreenStore(s => s.CreateFormModal?.[storeKey]) || EMPTY_DRAFT;

    const router = useRouter();
    const pathname = usePathname();
    const queryClient = useQueryClient();
    const { data: loggedInUser } = Query.people.loggedInUser();
    const formFoldersQuery = Query.formFolders.all();
    const formFolders = formFoldersQuery.data || [];
    const folderChoices = useMemo(() => flattenFolderTree(buildFolderTree(formFolders)).map((folder) => ({ ...folder, name: folderPath(formFolders, folder.id) })), [formFolders]);
    const selectedFolder = folderChoices.find((folder) => folder.id === (details.folderID || folderID)) || null;
    const folderQuery = { ...formFoldersQuery, data: folderChoices };
    const { data: formToEdit } = Query.forms.byId({ id, options: { enabled: !!id } });

    useEffect(() => {
        if (formToEdit && !Object.keys(details).length) {
            // Map FormRoles junction rows → flat Roles array for the UI
            const roles = (formToEdit.FormRoles || []).map((fr) => ({
                id: fr.roleID,
                name: fr.Role?.name,
                canView: fr.canView ?? true,
                canEdit: fr.canEdit ?? false,
                canManageEntries: fr.canManageEntries ?? false,
            }));
            setEditScreenData('CreateFormModal', storeKey, {
                ...formToEdit,
                Roles: roles,
                // Normalized snapshot — saveForm compares originalValues.schema
                // against the payload to decide the schemaVersion bump, so a
                // raw null schema would bump the version on every save.
                originalValues: { ...formToEdit, Roles: roles, schema: formToEdit.schema || createEmptySchema() },
            });
        }
    }, [formToEdit]);

    const setDetails = (newDetails) => {
        setEditScreenData('CreateFormModal', storeKey, newDetails);
    };

    const saveFormMutation = useMutation({
        mutationFn: saveForm,
        onSuccess: (data) => {
            removeEditScreenData('CreateFormModal', storeKey);
            queryClient.invalidateQueries({ queryKey: QueryKeys.forms.all() });
            if (id) {
                queryClient.invalidateQueries({ queryKey: QueryKeys.forms.byId(id) });
            }
            if (!id && data?.id) {
                router.back();
                setTimeout(() => router.push('/forms/' + data.id), 100);
            } else {
                redirectToDefault(router, 'CreateFormModal');
            }
        },
    });

    const handleSave = () => {
        if (!areStringsPopulated([details.title])) {
            Toast.show({ type: 'error', text2: 'Please enter a title for this form.' });
            return;
        }

        const { originalValues, ...cleanDetails } = details;
        saveFormMutation.mutate({
            details: {
                ...cleanDetails,
                id: id || undefined,
                title: cleanDetails.title,
                description: cleanDetails.description,
                isPublic: cleanDetails.isPublic ?? false,
                autoCreatePeople: cleanDetails.autoCreatePeople ?? false,
                folderID: cleanDetails.folderID || folderID || null,
                schema: cleanDetails.schema || createEmptySchema(),
                createdBy: !id ? loggedInUser?.id : undefined,
            },
            originalValues,
        });
    };

    return (
        <ModalWrapper
            title={id ? 'Edit Form' : 'Create Form'}
            confirmText="Save"
            loading={saveFormMutation.isPending}
            onConfirm={handleSave}
            onCancel={() => removeEditScreenData('CreateFormModal', storeKey)}
        >
            <KeyboardAwareScrollView>
                <Input
                    isNotOnSurface
                    title="Form Title"
                    placeholder="e.g. New Visitor Registration"
                    value={details.title || ''}
                    onUpdate={(val) => setDetails({ ...details, title: val })}
                />

                <Input
                    isNotOnSurface
                    title="Description"
                    placeholder="Describe the purpose of this form..."
                    multiline
                    value={details.description || ''}
                    onUpdate={(val) => setDetails({ ...details, description: val })}
                />

                <SheetSelector
                    isNotOnSurface
                    title="Folder"
                    placeholder="Uncategorized"
                    helpText="Folders organize the library only and never change form access."
                    type="roleFolders"
                    queryResult={folderQuery}
                    value={selectedFolder}
                    onChange={(folder) => setDetails({ ...details, folderID: folder?.id || null })}
                />

                <ToggleRow
                    label="Public Form"
                    description="When on, this form gets a shareable public link anyone can fill out without logging in."
                    value={details.isPublic ?? false}
                    onValueChange={(val) => setDetails({ ...details, isPublic: val })}
                />

                <FormRolesPicker
                    value={details.Roles || []}
                    onChange={(roles) => setDetails({ ...details, Roles: roles })}
                    isPublic={details.isPublic ?? false}
                    fromScreen={pathname}
                    fromStoreKey={storeKey}
                />

                <ToggleRow
                    label="Auto-Create New People"
                    description="When on, a new Person record is created automatically when a submission has no name match."
                    value={details.autoCreatePeople ?? false}
                    onValueChange={(val) => setDetails({ ...details, autoCreatePeople: val })}
                />
            </KeyboardAwareScrollView>
        </ModalWrapper>
    );
}
